import { BASE_URL } from '~/service/constants';

/**
 * Standard JSON API headers with bearer auth and optional CSRF.
 * @param {string} jwt - JWT token
 * @param {string} [csrf] - CSRF token; omitted from the headers when falsy
 */
export const apiHeaders = (jwt, csrf) => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  Authorization: `Bearer ${jwt}`,
  ...(csrf && { 'X-CSRF-Token': csrf }),
});

/**
 * Fire a JSON POST to an API path with auth headers. Returns the fetch promise
 * so callers decide how to handle the response (await, swallow, etc.).
 * @param {string} path - API path appended to BASE_URL
 * @param {object} body - JSON-serializable request body
 * @param {string} jwt - JWT token
 * @param {string} [csrf] - CSRF token
 * @param {{ keepalive?: boolean }} [options]
 * @returns {Promise<Response>}
 */
export const postEvent = (path, body, jwt, csrf, { keepalive = false } = {}) =>
  fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: apiHeaders(jwt, csrf),
    body: JSON.stringify(body),
    keepalive,
  });

/**
 * Parse a failed API response and throw with the most useful message available.
 * `message` is checked before `error` because some endpoints (e.g. the paywall
 * restriction on canceled accounts) put a machine-readable code in `error` and
 * the human-readable text in `message` - prefer that when both are present.
 * @param {Response} response - fetch Response with response.ok === false
 * @param {string} [defaultMessage] - fallback when the body has no recognisable error field
 */
export const handleApiError = async (response, defaultMessage = 'Request failed') => {
  let errorData;
  try {
    errorData = await response.json();
  } catch {
    throw new Error(`${defaultMessage} (HTTP ${response.status})`);
  }
  throw new Error(
    errorData?.errors?.[0] || errorData?.message || errorData?.error || defaultMessage
  );
};
